import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Header from '../Header';
import BottomNavigationBar from '../BottomNavigationBar';
import ChatRoomList from './ChatRoomList';
import ChatWindow from './ChatWindow';
import chatService from '../../services/chatService';
import './ChatLayout.css';

const ChatLayout = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [rooms, setRooms] = useState([]);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showRoomView, setShowRoomView] = useState(false); // For mobile: track if showing room view
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const isLoadingRef = useRef(false);
  const lastLoadTimeRef = useRef(0);
  
  // Handle window resize for mobile detection
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
      // If switching to desktop, reset room view state
      if (window.innerWidth > 768) {
        setShowRoomView(false);
      }
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Memoize loadChatRooms to prevent infinite loops
  const loadChatRooms = useCallback(async (force = false) => {
    // Prevent multiple simultaneous loads
    if (isLoadingRef.current && !force) {
      return;
    }

    // Throttle: Don't reload if last load was less than 2 seconds ago (unless forced)
    const now = Date.now();
    if (!force && (now - lastLoadTimeRef.current) < 2000) {
      return;
    }

    try {
      isLoadingRef.current = true;
      lastLoadTimeRef.current = now;
      setLoading(true);
      const response = await chatService.getChatRooms();
      if (response.success) {
        setRooms(response.data);
        // Auto-select first room if available and no room is selected (only on desktop)
        if (!isMobile) {
          setSelectedRoom(prev => {
            if (!prev && response.data.length > 0) {
              return response.data[0];
            }
            return prev;
          });
        }
      }
    } catch (err) {
      setError(err.message || 'Failed to load chat rooms');
      console.error('Error loading chat rooms:', err);
    } finally {
      setLoading(false);
      isLoadingRef.current = false;
    }
  }, []);

  useEffect(() => {
    // Load rooms only once on mount
    loadChatRooms(true);
    
    // Update user status on mount (only once, debounced)
    const initialStatusTimeout = setTimeout(() => {
      chatService.updateUserStatus(true);
    }, 1000); // Small delay to debounce
    
    // Update status periodically (reduced frequency - only update last_seen_at, not broadcast)
    // Status changes (online/offline) are handled by popup open/close, not polling
    const statusInterval = setInterval(() => {
      // Only update last_seen_at, don't change online status (prevents unnecessary Pusher broadcasts)
      chatService.updateUserStatus(true).catch(() => {
        // Silently fail - not critical
      });
    }, 120000); // Every 2 minutes (reduced from 30 seconds)

    let newMessageTimeout = null;

    // Listen for new chat room creation
    const handleChatRoomCreated = () => {
      loadChatRooms(true); // Force reload when new room is created
    };

    // Listen for unread count updates
    const handleUnreadCountUpdate = (event) => {
      const data = event.detail;
      if (data && data.unread_counts) {
        // Update unread counts in rooms without reloading
        setRooms(prevRooms => {
          return prevRooms.map(room => {
            const unreadCount = data.unread_counts[room.id] || 0;
            return {
              ...room,
              unread_count: unreadCount
            };
          });
        });
      }
    };

    // Listen for new messages - update room list and move to top (WhatsApp-like)
    // NO RELOAD - just update state
    const handleNewMessage = (event) => {
      const data = event.detail;
      if (data && data.room_id && data.message) {
        const isOwnMessage = data.message.user_id === user?.id;
        
        // Skip updating room list for own messages - they're already shown optimistically
        // Only update for messages from others
        if (!isOwnMessage) {
          // Update the room's latest message and move it to the top - NO RELOAD
          setRooms(prevRooms => {
            // Find the room that received the message
            const roomIndex = prevRooms.findIndex(room => room.id === data.room_id);
            
            if (roomIndex === -1) {
              // Room not found, might be a new room - add it to the list without reload
              const newRoom = {
                id: data.room_id,
                latest_message: data.message,
                updated_at: data.message.created_at,
                unread_count: 1,
                // You might need to fetch room details if needed, but don't reload all rooms
              };
              return [newRoom, ...prevRooms];
            }
            
            // Create updated room with new latest message
            const updatedRoom = {
              ...prevRooms[roomIndex],
              latest_message: data.message,
              updated_at: data.message.created_at,
              // Increment unread count for messages from others
              unread_count: (prevRooms[roomIndex].unread_count || 0) + 1
            };
            
            // Remove room from its current position and add to top - NO RELOAD
            // This ensures the room with the latest message is always at the top
            const newRooms = [
              updatedRoom,
              ...prevRooms.filter(room => room.id !== data.room_id)
            ];
            
            // Don't update selectedRoom for received messages - it causes reload
            // The room list update is enough, and ChatWindow will handle the message via Pusher
            
            return newRooms;
          });
        } else {
          // For own messages, just update the latest_message in the room list without moving it
          // This prevents unnecessary re-renders
          setRooms(prevRooms => {
            return prevRooms.map(room => {
              if (room.id === data.room_id) {
                return {
                  ...room,
                  latest_message: data.message,
                  updated_at: data.message.created_at
                };
              }
              return room;
            });
          });
        }
      }
    };

    // Listen for user online/offline events
    const handleUserOnline = (event) => {
      const data = event.detail;
      if (data && data.user_id) {
        // Update online status in rooms
        setRooms(prevRooms => {
          return prevRooms.map(room => {
            // Check if this user is a peer in this room
            const updatedPeers = room.peers?.map(peer => {
              if (peer.id === data.user_id) {
                return {
                  ...peer,
                  is_online: true,
                  last_seen_at: data.last_seen_at,
                };
              }
              return peer;
            });
            
            if (updatedPeers) {
              return {
                ...room,
                peers: updatedPeers,
              };
            }
            return room;
          });
        });

        // Update selected room if it contains this user
        setSelectedRoom(prevRoom => {
          if (!prevRoom) return prevRoom;
          const updatedPeers = prevRoom.peers?.map(peer => {
            if (peer.id === data.user_id) {
              return {
                ...peer,
                is_online: true,
                last_seen_at: data.last_seen_at,
              };
            }
            return peer;
          });
          
          if (updatedPeers) {
            return {
              ...prevRoom,
              peers: updatedPeers,
            };
          }
          return prevRoom;
        });
      }
    };

    const handleUserOffline = (event) => {
      const data = event.detail;
      if (data && data.user_id) {
        // Update offline status in rooms
        setRooms(prevRooms => {
          return prevRooms.map(room => {
            // Check if this user is a peer in this room
            const updatedPeers = room.peers?.map(peer => {
              if (peer.id === data.user_id) {
                return {
                  ...peer,
                  is_online: false,
                  last_seen_at: data.last_seen_at,
                };
              }
              return peer;
            });
            
            if (updatedPeers) {
              return {
                ...room,
                peers: updatedPeers,
              };
            }
            return room;
          });
        });

        // Update selected room if it contains this user
        setSelectedRoom(prevRoom => {
          if (!prevRoom) return prevRoom;
          const updatedPeers = prevRoom.peers?.map(peer => {
            if (peer.id === data.user_id) {
              return {
                ...peer,
                is_online: false,
                last_seen_at: data.last_seen_at,
              };
            }
            return peer;
          });
          
          if (updatedPeers) {
            return {
              ...prevRoom,
              peers: updatedPeers,
            };
          }
          return prevRoom;
        });
      }
    };

    window.addEventListener('pusher:chat-room-created', handleChatRoomCreated);
    window.addEventListener('pusher:chat-unread-count-updated', handleUnreadCountUpdate);
    window.addEventListener('pusher:new-message', handleNewMessage);
    window.addEventListener('pusher:user-online', handleUserOnline);
    window.addEventListener('pusher:user-offline', handleUserOffline);

    return () => {
      clearTimeout(initialStatusTimeout);
      clearInterval(statusInterval);
      if (newMessageTimeout) {
        clearTimeout(newMessageTimeout);
      }
      window.removeEventListener('pusher:chat-room-created', handleChatRoomCreated);
      window.removeEventListener('pusher:chat-unread-count-updated', handleUnreadCountUpdate);
      window.removeEventListener('pusher:new-message', handleNewMessage);
      window.removeEventListener('pusher:user-online', handleUserOnline);
      window.removeEventListener('pusher:user-offline', handleUserOffline);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array - only run once on mount

  const handleRoomSelect = async (room) => {
    setSelectedRoom(room);
    // On mobile, show room view
    if (isMobile) {
      setShowRoomView(true);
    }
    // Mark messages as read when opening a room
    // Unread count will be updated via Pusher event
    try {
      await chatService.markMessagesAsRead(room.id);
    } catch (err) {
      console.error('Error marking messages as read:', err);
    }
  };
  
  const handleBackToList = () => {
    setShowRoomView(false);
  };

  // Format timestamp for chat room list (WhatsApp-like: Today, Yesterday, or date)
  const formatChatRoomTime = (dateString) => {
    if (!dateString) return '';
    
    const date = new Date(dateString);
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const messageDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    
    // Check if message is from today
    if (messageDate.getTime() === today.getTime()) {
      return date.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      });
    }
    
    // Check if message is from yesterday
    if (messageDate.getTime() === yesterday.getTime()) {
      return 'Yesterday';
    }
    
    // Check if message is from this week (within 7 days)
    const daysDiff = Math.floor((today - messageDate) / (1000 * 60 * 60 * 24));
    if (daysDiff < 7) {
      return date.toLocaleDateString('en-US', { weekday: 'short' });
    }
    
    // Otherwise show date
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
    });
  };

  // Memoize handleNewMessage to prevent unnecessary re-renders
  const handleNewMessage = useCallback(() => {
    // Don't reload immediately - let Pusher events handle updates
    // Only update if needed
  }, []);

  // Navigation handlers
  const handleHomeClick = () => navigate('/');
  const handleCategoriesClick = () => navigate('/categories');
  const handleHowItWorksClick = () => {
    navigate('/');
    setTimeout(() => {
      document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };
  const handleProfileClick = () => navigate('/profile');
  const handleAccountSettingsClick = () => navigate('/settings');
  const handleForumClick = () => navigate('/forum');
  const handleJobsClick = () => navigate('/vacancies');
  const handleMessagesClick = () => navigate('/chat');
  const handleNotificationsClick = () => navigate('/notifications');
  const handleLoginClick = () => navigate('/login');
  const handleSignupClick = () => navigate('/signup');

  if (loading) {
    return (
      <div className="chat-page chat-page-with-bottom-nav">
        {/* Header - visible on desktop/tablet, hidden on mobile */}
        <div className="chat-page-header">
          <Header
            onLoginClick={handleLoginClick}
            onSignupClick={handleSignupClick}
            isLoggedIn={isAuthenticated}
            user={user}
            onHomeClick={handleHomeClick}
            onCategoriesClick={handleCategoriesClick}
            onHowItWorksClick={handleHowItWorksClick}
            onProfileClick={handleProfileClick}
            onAccountSettingsClick={handleAccountSettingsClick}
            onForumClick={handleForumClick}
            onJobsClick={handleJobsClick}
            currentPage="chat"
          />
        </div>
        <div className="chat-layout">
          <div className="chat-loading">
            <div className="spinner"></div>
            <p>Loading chats...</p>
          </div>
        </div>
        {/* Bottom Navigation Bar */}
        <BottomNavigationBar
          onHomeClick={handleHomeClick}
          onLoginClick={handleLoginClick}
          onSignupClick={handleSignupClick}
          onProfileClick={handleProfileClick}
          onAccountSettingsClick={handleAccountSettingsClick}
          onJobsClick={handleJobsClick}
          onMessagesClick={handleMessagesClick}
          onNotificationsClick={handleNotificationsClick}
          onForumClick={handleForumClick}
        />
      </div>
    );
  }

  if (error) {
    return (
      <div className="chat-page chat-page-with-bottom-nav">
        {/* Header - visible on desktop/tablet, hidden on mobile */}
        <div className="chat-page-header">
          <Header
            onLoginClick={handleLoginClick}
            onSignupClick={handleSignupClick}
            isLoggedIn={isAuthenticated}
            user={user}
            onHomeClick={handleHomeClick}
            onCategoriesClick={handleCategoriesClick}
            onHowItWorksClick={handleHowItWorksClick}
            onProfileClick={handleProfileClick}
            onAccountSettingsClick={handleAccountSettingsClick}
            onForumClick={handleForumClick}
            onJobsClick={handleJobsClick}
            currentPage="chat"
          />
        </div>
        <div className="chat-layout">
          <div className="chat-error">
            <p>{error}</p>
            <button onClick={loadChatRooms}>Retry</button>
          </div>
        </div>
        {/* Bottom Navigation Bar */}
        <BottomNavigationBar
          onHomeClick={handleHomeClick}
          onLoginClick={handleLoginClick}
          onSignupClick={handleSignupClick}
          onProfileClick={handleProfileClick}
          onAccountSettingsClick={handleAccountSettingsClick}
          onJobsClick={handleJobsClick}
          onMessagesClick={handleMessagesClick}
          onNotificationsClick={handleNotificationsClick}
          onForumClick={handleForumClick}
        />
      </div>
    );
  }

  return (
    <div className="chat-page chat-page-with-bottom-nav">
      {/* Header - visible on desktop/tablet, hidden on mobile */}
      <div className="chat-page-header">
        <Header
          onLoginClick={handleLoginClick}
          onSignupClick={handleSignupClick}
          isLoggedIn={isAuthenticated}
          user={user}
          onHomeClick={handleHomeClick}
          onCategoriesClick={handleCategoriesClick}
          onHowItWorksClick={handleHowItWorksClick}
          onProfileClick={handleProfileClick}
          onAccountSettingsClick={handleAccountSettingsClick}
          onForumClick={handleForumClick}
          onJobsClick={handleJobsClick}
          currentPage="chat"
        />
      </div>
      
      <div className="chat-container">
        <div className="chat-layout">
          {/* Left Sidebar - Chat Rooms List */}
          <div className={`chat-sidebar ${isMobile && showRoomView ? 'mobile-hidden' : ''}`}>
            <div className="chat-sidebar-header">
              <h2>Chats</h2>
            </div>
            
            <div className="chat-sidebar-search">
              <input
                type="text"
                placeholder="Search chats..."
                className="chat-search-input"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>

            {loading ? (
              <div className="chat-sidebar-loading">
                <div className="chat-spinner"></div>
                <span>Loading chats...</span>
              </div>
            ) : rooms.length === 0 ? (
              <div className="chat-sidebar-empty">
                <div className="chat-empty-icon">
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                  </svg>
                </div>
                <h3>No chats yet</h3>
                <p>Start a conversation to see it here</p>
              </div>
            ) : (
              <div className="chat-rooms-list">
                {rooms
                  .filter(room => {
                    if (!searchQuery) return true;
                    const query = searchQuery.toLowerCase();
                    return (
                      room.name?.toLowerCase().includes(query) ||
                      (room.peers && room.peers.some(peer => peer.name?.toLowerCase().includes(query)))
                    );
                  })
                  .map((room) => (
                  <div
                    key={room.id}
                    className={`chat-room-item ${selectedRoom?.id === room.id ? 'active' : ''}`}
                    onClick={() => handleRoomSelect(room)}
                  >
                    <div className="chat-room-item-avatar">
                      {room.peers && room.peers.length > 0 && room.peers[0].profile_photo ? (
                        <img src={room.peers[0].profile_photo} alt={room.name} />
                      ) : (
                        <div className="chat-room-avatar-placeholder">
                          {room.name?.charAt(0).toUpperCase() || 'C'}
                        </div>
                      )}
                      {room.unread_count > 0 && (
                        <span className="chat-unread-badge">{room.unread_count}</span>
                      )}
                    </div>
                    
                    <div className="chat-room-item-content">
                      <div className="chat-room-item-header">
                        <h3 className="chat-room-item-title">
                          {room.peers && room.peers.length > 0 ? room.peers[0].name : room.name}
                        </h3>
                        {room.latest_message && (
                          <span className="chat-room-item-time">
                            {formatChatRoomTime(room.latest_message.created_at)}
                          </span>
                        )}
                      </div>
                      
                      {room.latest_message && (
                        <div className="chat-room-item-preview">
                          <span className="chat-room-item-message">
                            {room.latest_message.user_id === user?.id ? 'You: ' : ''}
                            {room.latest_message.message}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                  ))}
              </div>
            )}
          </div>

          {/* Right Panel - Chat Window */}
          <div className={`chat-detail-panel ${isMobile && !showRoomView ? 'mobile-hidden' : ''}`}>
            {isMobile && showRoomView && (
              <button className="chat-back-button" onClick={handleBackToList}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M19 12H5"></path>
                  <path d="M12 19l-7-7 7-7"></path>
                </svg>
                Back
              </button>
            )}
            <ChatWindow
              room={selectedRoom}
              onNewMessage={handleNewMessage}
            />
          </div>
        </div>
      </div>

      {/* Bottom Navigation Bar */}
      <BottomNavigationBar
        onHomeClick={handleHomeClick}
        onLoginClick={handleLoginClick}
        onSignupClick={handleSignupClick}
        onProfileClick={handleProfileClick}
        onAccountSettingsClick={handleAccountSettingsClick}
        onJobsClick={handleJobsClick}
        onMessagesClick={handleMessagesClick}
        onNotificationsClick={handleNotificationsClick}
        onForumClick={handleForumClick}
      />
    </div>
  );
};

export default ChatLayout;

